import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  Link,
  useLocation,
} from "react-router-dom";
import { useState, useEffect } from "react";
import LoginPage from "./components/login/LoginPage";
import PrivateRoute from "./components/login/PrivateRoute";
import HomePage from "./components/HomePage";
import OrderDetailsPage from "./components/order/OrderDetailsPage";
import Orders from "./components/order/Orders";
import ProductListPage from "./components/product/ProductPage";
import AddProduct from "./components/product/AddProduct";
import PricingPage from "./components/pricing/PricingPage";
import VendorQuotationPage from "./components/quote/VendorQuotationPage";
import OffersPage from "./components/OfferPage";
import VendorReviewPage from "./components/vendor/VendorReviewPage";
import UserProfileMenu from "./components/userProfileMenu/UserProfileMenu";
import { ChevronDown, ChevronRight } from "lucide-react";
import { VendorProvider, useVendorContext } from "./context/VendorContext";
import ProfilePage from "./components/profile/ProfilePage";
import { Product } from './types/ProductsForm';
import {apiRequest} from './utils/apiClient';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [allProductsList, setAllProductsList] = useState<Product[]>([]);

  const { vendor,role } = useVendorContext();


    useEffect(() => {
      const fetchAllProducts = async () => {
        try {
          const response = await apiRequest(`/products`);
          setAllProductsList(response);
        } catch (error) {
          console.error("Error fetching products:", error);
        }
      };
      fetchAllProducts();
    }, []);

  const isAuthenticated = () => !!localStorage.getItem("jwtToken");

  const toggleSection = (section: string) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  const handleLogout = () => {
    localStorage.removeItem("jwtToken");
    navigate("/login");
  };

  const pathnames = location.pathname.split("/").filter((x) => x);
  const breadcrumbMap: Record<string, string> = {
    "products": "Products",
    "add-product": "Add Product",
    "orders": "Orders",
    "pricing": "Pricing",
    "offer": "Offers",
    "customization-studio": "Quotation",
    "vendor-review": "Vendor Review",
  };

  const getBreadcrumbs = () => (
    <nav className="bg-gray-200 p-1 rounded-xl px-3 text-xs text-gray-600 mt-2">
      <ol className="flex space-x-1">
        <li>
          <Link to="/" className="hover:underline text-blue-600">Home</Link>
        </li>
        {pathnames.map((value, index) => {
          const to = "/" + pathnames.slice(0, index + 1).join("/");
          const label = breadcrumbMap[value] || value;
          return (
            <li key={to} className="flex items-center space-x-1">
              <span className="mx-1">/</span>
              {index === pathnames.length - 1 ? (
                <span className="text-gray-800 font-semibold">{label}</span>
              ) : (
                <Link to={to} className="hover:underline text-blue-600">{label}</Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );

  function clsx(base: string, conditional: { [key: string]: boolean }): string {
    const classes = [base];
    for (const key in conditional) {
      if (conditional[key]) {
        classes.push(key);
      }
    }
    return classes.join(" ");
  }

  return (
    <div className="App flex min-h-screen">
      {isAuthenticated() && location.pathname !== '/login' && (
        <aside className="fixed left-0 top-0 w-64 h-screen bg-gray-800 text-white p-6 overflow-y-auto z-50 flex flex-col justify-between border-r border-gray-700 shadow-lg">
          <div className="mb-8 flex items-center gap-4">
            <img src="/icon.png" alt="Aurum Console" className="h-10 rounded-full" />
            <h2
              className="text-lg font-bold cursor-pointer text-yellow-400"
              onClick={() => navigate("/")}
            >
              Aurum Console
            </h2>
          </div>

          <nav className="flex-1">
            <ul className="flex flex-col gap-4">

              {/* Orders - all roles */}
              <li>
                <button
                  onClick={() => toggleSection("orders")}
                  className="w-full flex justify-between items-center text-left hover:text-yellow-400 font-semibold"
                >
                  <span>Orders</span>
                  {openSection === "orders" ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {openSection === "orders" && (
                  <ul className="ml-4 mt-2 flex flex-col gap-2 text-sm">
                    <li>
                      <Link to="/orders" className="hover:text-yellow-300">Website Orders</Link>
                    </li>
                  </ul>
                )}
              </li>

              {/* Products - all roles */}
              <li>
                <button
                  onClick={() => toggleSection("products")}
                  className="w-full flex justify-between items-center text-left hover:text-yellow-400 font-semibold"
                >
                  <span>Products</span>
                  {openSection === "products" ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {openSection === "products" && (
                  <ul className="ml-4 mt-2 flex flex-col gap-2 text-sm">
                    <li>
                      <Link to="/products" className="hover:text-yellow-300">View Products</Link>
                    </li>
                    <li>
                      <Link to="/add-product" className="hover:text-yellow-300">Add Product</Link>
                    </li>
                  </ul>
                )}
              </li>

              {/* Pricing - all roles */}
              <li>
                <Link to="/pricing" className="hover:text-yellow-400">
                  Pricing
                </Link>
              </li>

              {/* COMPANY-only */}
              {role === "COMPANY" && (
                <>
                  <li>
                    <Link to="/vendor-review" className="hover:text-yellow-400">
                      Vendor Review
                    </Link>
                  </li>
                  <li>
                    <Link to="/offer" className="hover:text-yellow-400">
                      Offers
                    </Link>
                  </li>
                  <li>
                    <Link to="/customization-studio" className="hover:text-yellow-400">
                      Quotation
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </nav>
        </aside>
      )}

      <div className="min-h-full min-w-full">
        {isAuthenticated() && location.pathname !== '/login' && (
          <div className="fixed top-0 left-0 right-0 h-20 bg-gray-800 border-b border-gray-700 shadow-md z-10 text-white flex items-center justify-between px-6 ml-64">
            <div>{getBreadcrumbs()}</div>
            <div className="flex items-center gap-3">
              <UserProfileMenu />
            </div>
          </div>
        )}

        <main className={clsx("flex-1 bg-gray-200 p-6 min-h-full", {
          "ml-64 mt-20": isAuthenticated() && location.pathname !== '/login',
        })}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<PrivateRoute><HomePage /></PrivateRoute>} />
            <Route path="/orders" element={<PrivateRoute><Orders /></PrivateRoute>} />
            <Route path="/orders/:id" element={<PrivateRoute><OrderDetailsPage /></PrivateRoute>} />
            <Route path="/products" element={<PrivateRoute><ProductListPage /></PrivateRoute>} />
            <Route path="/add-product" element={<PrivateRoute><AddProduct updateproduct={undefined} productList={allProductsList} /></PrivateRoute>} />
            <Route path="/pricing" element={<PrivateRoute><PricingPage /></PrivateRoute>} />
            <Route path="/offer" element={<PrivateRoute><OffersPage /></PrivateRoute>} />
            <Route path="/customization-studio" element={<PrivateRoute><VendorQuotationPage /></PrivateRoute>} />
            <Route path="/vendor-review" element={<PrivateRoute><VendorReviewPage /></PrivateRoute>} />
            <Route
            path="/profile"
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            }
          />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <VendorProvider>
      <AppContent />
    </VendorProvider>
  );
}
